import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Save, Clock, Calendar, Euro, Power, RefreshCw, CheckCircle, XCircle, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = [
  { key: "mon", label: "Montag" },
  { key: "tue", label: "Dienstag" },
  { key: "wed", label: "Mittwoch" },
  { key: "thu", label: "Donnerstag" },
  { key: "fri", label: "Freitag" },
  { key: "sat", label: "Samstag" },
  { key: "sun", label: "Sonntag" },
];

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2, 8));

const emptyService = () => ({ id: uid(), name: "", duration_min: 60, price_eur: 0, description: "", active: true });

const defaultCfg = () => ({
  enabled: false,
  services: [],
  hours: Object.fromEntries(WEEKDAYS.map(w => [w.key, { closed: w.key === "sun", open: "09:00", close: "18:00", break_start: "", break_end: "" }])),
  slot_interval_min: 30,
  lead_time_hours: 2,
  max_days_ahead: 30,
  timezone: "Europe/Berlin",
  location_text: "",
});

const STATUS_COLORS = {
  neu: "bg-amber-100 text-amber-900 border-amber-300",
  bestätigt: "bg-emerald-100 text-emerald-900 border-emerald-300",
  confirmed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  abgesagt: "bg-red-100 text-red-900 border-red-300",
  cancelled: "bg-red-100 text-red-900 border-red-300",
  erledigt: "bg-slate-200 text-slate-700 border-slate-300",
  completed: "bg-slate-200 text-slate-700 border-slate-300",
};

export const BookingEditor = ({ projectId, companyId }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [activeTab, setActiveTab] = useState("config"); // "config" | "bookings"

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    axios.get(`${API}/projects/${projectId}/booking-config`, { withCredentials: true })
      .then(res => setCfg({ ...defaultCfg(), ...res.data }))
      .catch(() => setCfg(defaultCfg()))
      .finally(() => setLoading(false));
  }, [projectId]);

  const loadBookings = async () => {
    if (!companyId) return;
    setLoadingBookings(true);
    try {
      const res = await axios.get(`${API}/bookings/company/${companyId}`, { withCredentials: true });
      setBookings(res.data || []);
    } catch {
      toast.error("Buchungen konnten nicht geladen werden");
    } finally {
      setLoadingBookings(false);
    }
  };

  useEffect(() => {
    if (activeTab === "bookings") loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, companyId]);

  if (loading || !cfg) {
    return <div className="p-6 text-sm text-muted-foreground">Lade Buchungs-Konfiguration...</div>;
  }

  const patch = (upd) => setCfg(prev => ({ ...prev, ...upd }));
  const updateHours = (wd, upd) => setCfg(prev => ({ ...prev, hours: { ...prev.hours, [wd]: { ...prev.hours[wd], ...upd } } }));

  const addService = () => patch({ services: [...cfg.services, emptyService()] });
  const updateService = (idx, upd) => patch({ services: cfg.services.map((s, i) => i === idx ? { ...s, ...upd } : s) });
  const removeService = (idx) => patch({ services: cfg.services.filter((_, i) => i !== idx) });

  const handleSave = async () => {
    if (cfg.enabled && cfg.services.filter(s => s.name.trim()).length === 0) {
      toast.error("Mindestens eine Leistung nötig, wenn das Buchungssystem aktiv ist");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/projects/${projectId}/booking-config`, cfg, { withCredentials: true, timeout: 60000 });
      setCfg({ ...defaultCfg(), ...res.data });
      toast.success("Buchungs-Konfiguration gespeichert – Vorschau aktualisiert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.put(`${API}/bookings/${id}`, { status }, { withCredentials: true });
      setBookings(bs => bs.map(b => b.id === id ? { ...b, status } : b));
      toast.success("Status aktualisiert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Buchung wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, { withCredentials: true });
      setBookings(bs => bs.filter(b => b.id !== id));
      toast.success("Gelöscht");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-6" data-testid="booking-editor">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        <button type="button" onClick={() => setActiveTab("config")} data-testid="booking-tab-config"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "config" ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          Konfiguration
        </button>
        <button type="button" onClick={() => setActiveTab("bookings")} data-testid="booking-tab-list"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${activeTab === "bookings" ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          Terminliste
          {bookings.filter(b => b.status === "neu").length > 0 && (
            <span className="bg-amber-500 text-white text-[10px] px-1.5 rounded-full min-w-[18px] text-center">
              {bookings.filter(b => b.status === "neu").length}
            </span>
          )}
        </button>
      </div>

      {activeTab === "config" && (
        <>
          {/* Enable */}
          <div className="flex items-center justify-between p-4 rounded-sm border border-border bg-muted/30">
            <div>
              <div className="flex items-center gap-2 font-semibold text-sm"><Power size={14} /> Online-Terminbuchung</div>
              <p className="text-xs text-muted-foreground mt-0.5">Aktiviere das Buchungs-Widget auf deiner Website.</p>
            </div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => patch({ enabled: v })} data-testid="booking-enabled-switch" />
          </div>

          {/* Services */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Leistungen</Label>
              <Button size="sm" variant="outline" onClick={addService} className="h-7 rounded-sm text-xs" data-testid="add-service-btn">
                <Plus size={12} className="mr-1" /> Leistung
              </Button>
            </div>
            {cfg.services.length === 0 && (
              <div className="text-xs text-muted-foreground p-4 text-center border border-dashed border-border rounded-sm">
                Noch keine Leistungen — Kunden können nur buchen, wenn mind. eine Leistung hinterlegt ist.
              </div>
            )}
            {cfg.services.map((s, idx) => (
              <div key={s.id} className="p-3 border border-border rounded-sm space-y-2" data-testid={`service-row-${idx}`}>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12 md:col-span-6">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Name *</Label>
                    <Input value={s.name} onChange={e => updateService(idx, { name: e.target.value })} placeholder="z.B. Damenhaarschnitt" className="rounded-sm h-8 text-sm" data-testid={`service-name-${idx}`} />
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Clock size={10}/> Dauer (Min.)</Label>
                    <Input type="number" min="5" step="5" value={s.duration_min} onChange={e => updateService(idx, { duration_min: parseInt(e.target.value) || 60 })} className="rounded-sm h-8 text-sm" data-testid={`service-duration-${idx}`} />
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Euro size={10}/> Preis (€)</Label>
                    <Input type="number" min="0" step="0.5" value={s.price_eur} onChange={e => updateService(idx, { price_eur: parseFloat(e.target.value) || 0 })} className="rounded-sm h-8 text-sm" data-testid={`service-price-${idx}`} />
                  </div>
                  <div className="col-span-12 md:col-span-1 flex items-end">
                    <Button size="sm" variant="ghost" onClick={() => removeService(idx)} className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600" data-testid={`remove-service-${idx}`}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Beschreibung (optional)</Label>
                  <Textarea value={s.description || ""} onChange={e => updateService(idx, { description: e.target.value })} rows={2} className="rounded-sm text-sm" placeholder="Kurze Info für Kunden" data-testid={`service-desc-${idx}`} />
                </div>
              </div>
            ))}
          </div>

          {/* Hours */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Öffnungszeiten</Label>
            <div className="border border-border rounded-sm overflow-hidden">
              {WEEKDAYS.map(w => {
                const h = cfg.hours[w.key] || {};
                return (
                  <div key={w.key} className="grid grid-cols-12 gap-2 p-2 items-center border-b border-border last:border-b-0 text-sm">
                    <div className="col-span-3 md:col-span-2 font-medium">{w.label}</div>
                    <div className="col-span-2 md:col-span-2 flex items-center gap-1.5">
                      <Switch checked={!h.closed} onCheckedChange={(v) => updateHours(w.key, { closed: !v })} data-testid={`hours-open-${w.key}`} />
                      <span className="text-xs text-muted-foreground">{h.closed ? "zu" : "offen"}</span>
                    </div>
                    <div className="col-span-7 md:col-span-8 grid grid-cols-4 gap-1">
                      <Input type="time" value={h.open || ""} onChange={e => updateHours(w.key, { open: e.target.value })} disabled={h.closed} className="rounded-sm h-7 text-xs" data-testid={`hours-from-${w.key}`} />
                      <Input type="time" value={h.close || ""} onChange={e => updateHours(w.key, { close: e.target.value })} disabled={h.closed} className="rounded-sm h-7 text-xs" data-testid={`hours-to-${w.key}`} />
                      <Input type="time" value={h.break_start || ""} onChange={e => updateHours(w.key, { break_start: e.target.value })} disabled={h.closed} className="rounded-sm h-7 text-xs" placeholder="Pause ab" title="Pause-Start (optional)" />
                      <Input type="time" value={h.break_end || ""} onChange={e => updateHours(w.key, { break_end: e.target.value })} disabled={h.closed} className="rounded-sm h-7 text-xs" placeholder="Pause bis" title="Pause-Ende (optional)" />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">Tipp: Pause-Felder optional — wenn gesetzt, wird dieses Intervall bei Online-Buchungen gesperrt.</p>
          </div>

          {/* Advanced */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Slot-Intervall</Label>
              <Select value={String(cfg.slot_interval_min)} onValueChange={(v) => patch({ slot_interval_min: parseInt(v) })}>
                <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="slot-interval-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 Minuten</SelectItem>
                  <SelectItem value="30">30 Minuten</SelectItem>
                  <SelectItem value="60">60 Minuten</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Vorlauf (Stunden)</Label>
              <Input type="number" min="0" max="72" value={cfg.lead_time_hours} onChange={e => patch({ lead_time_hours: parseInt(e.target.value) || 0 })} className="rounded-sm h-9 text-sm" data-testid="lead-time-input" />
            </div>
            <div>
              <Label className="text-xs">Max. Tage voraus</Label>
              <Input type="number" min="1" max="180" value={cfg.max_days_ahead} onChange={e => patch({ max_days_ahead: parseInt(e.target.value) || 30 })} className="rounded-sm h-9 text-sm" data-testid="max-days-input" />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-booking-config-btn">
              {saving ? <><RefreshCw size={14} className="mr-1 animate-spin" /> Speichert...</> : <><Save size={14} className="mr-1" /> Speichern & Vorschau aktualisieren</>}
            </Button>
          </div>
        </>
      )}

      {activeTab === "bookings" && (
        <div className="space-y-3" data-testid="bookings-list">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Termine ({bookings.length})</Label>
            <Button size="sm" variant="outline" onClick={loadBookings} className="h-7 rounded-sm text-xs">
              <RefreshCw size={11} className={`mr-1 ${loadingBookings ? "animate-spin" : ""}`} /> Aktualisieren
            </Button>
          </div>
          {loadingBookings && <p className="text-sm text-muted-foreground">Lade Buchungen...</p>}
          {!loadingBookings && bookings.length === 0 && (
            <div className="text-sm text-muted-foreground p-8 text-center border border-dashed border-border rounded-sm">
              Noch keine Buchungen eingegangen.
            </div>
          )}
          {bookings.map(b => {
            const status = b.status || "neu";
            const dateStr = b.preferred_date || "—";
            const timeStr = b.preferred_time || "";
            return (
              <div key={b.id} className="p-3 border border-border rounded-sm space-y-2" data-testid={`booking-row-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{b.name || "—"}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm border uppercase tracking-wider ${STATUS_COLORS[status] || STATUS_COLORS.neu}`}>{status}</span>
                      {b.source === "booking" && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200 uppercase">Online</span>}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="flex items-center gap-1"><Calendar size={11} /> {dateStr} {timeStr && `· ${timeStr}`}</span>
                      {b.service && <span>· {b.service}</span>}
                      {b.duration_min && <span>· {b.duration_min} Min.</span>}
                      {b.price_eur > 0 && <span>· {b.price_eur.toFixed(2)} €</span>}
                    </div>
                    <div className="mt-1 text-xs flex flex-wrap gap-x-3 gap-y-1">
                      {b.email && <a href={`mailto:${b.email}`} className="text-blue-600 hover:underline flex items-center gap-1"><Mail size={10}/> {b.email}</a>}
                      {b.phone && <a href={`tel:${b.phone}`} className="text-blue-600 hover:underline flex items-center gap-1"><Phone size={10}/> {b.phone}</a>}
                    </div>
                    {b.notes && <div className="mt-1 text-xs text-foreground bg-muted/50 p-1.5 rounded-sm">{b.notes}</div>}
                  </div>
                  <div className="flex flex-col gap-1 items-end shrink-0">
                    {status === "neu" && (
                      <Button size="sm" variant="outline" onClick={() => updateBookingStatus(b.id, "bestätigt")} className="h-7 rounded-sm text-xs" data-testid={`confirm-${b.id}`}>
                        <CheckCircle size={11} className="mr-1" /> Bestätigen
                      </Button>
                    )}
                    {status !== "abgesagt" && status !== "cancelled" && (
                      <Button size="sm" variant="ghost" onClick={() => updateBookingStatus(b.id, "abgesagt")} className="h-7 rounded-sm text-xs text-red-600 hover:text-red-700">
                        <XCircle size={11} className="mr-1" /> Absagen
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteBooking(b.id)} className="h-7 rounded-sm text-xs text-muted-foreground hover:text-red-600">
                      <Trash2 size={11} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
